import { useCallback } from 'react';
import { getPrimaryOriginalUrl } from '@/utils/canvasCropping';
import { updateAsset } from '@/utils/collectionsGraphQL';
import { getViewportCropUrl, generateAnnotatedImage } from '../utils/cropUtils';

// Map frontend enum values to GraphQL enum values
const mapComparisonModeToGraphQL = (mode: string) => {
  switch (mode) {
    case 'side-by-side': return 'side_by_side'; // legacy support
    case 'side_by_side': return 'side_by_side';
    case 'stacked': return 'stacked';
    case 'single': return 'single';
    default: return 'single';
  }
};

export const useSaveHandlers = (
  editorState: any,
  annotationState: any,
  asset: any,
  imageRef: React.RefObject<HTMLImageElement>,
  secondaryImageRef: React.RefObject<HTMLImageElement>,
  toast: any,
  onOpenChange: (open: boolean) => void,
  onSuccess?: () => void
) => {
  // Helper function to get effective primary original URL (same as debug panel)
  const getEffectivePrimaryOriginalUrl = useCallback(() => {
    if (editorState.primaryImageData?.imageUrl) {
      return editorState.primaryImageData.imageUrl;
    }
    return getPrimaryOriginalUrl(asset);
  }, [editorState.primaryImageData, asset]);

  // Helper function to get effective secondary original URL (same as debug panel)
  const getEffectiveSecondaryOriginalUrl = useCallback(() => {
    if (editorState.secondaryImageData?.imageUrl) {
      return editorState.secondaryImageData.imageUrl;
    }
    return editorState.secondaryImageData?.imageUrl || asset.secondary_original_url || asset.secondary_image || '';
  }, [editorState.secondaryImageData, asset]);

  // Helper function to generate crop URL (same as debug panel)
  const getPrimaryCropUrl = useCallback(() => {
    const originalImageUrl = getEffectivePrimaryOriginalUrl();
    if (!originalImageUrl || !imageRef.current) return null;
    
    return getViewportCropUrl(
      originalImageUrl, 
      editorState.zoomCoordinates, 
      undefined, 
      imageRef.current
    );
  }, [getEffectivePrimaryOriginalUrl, editorState.zoomCoordinates, imageRef]);

  // Helper function to generate secondary crop URL (same as debug panel)
  const getSecondaryCropUrl = useCallback(() => {
    const secondaryImageUrl = getEffectiveSecondaryOriginalUrl();
    if (!secondaryImageUrl || !secondaryImageRef.current) return null;
    
    return getViewportCropUrl(
      secondaryImageUrl, 
      editorState.secondaryZoomCoordinates, 
      undefined, 
      secondaryImageRef.current
    );
  }, [getEffectiveSecondaryOriginalUrl, editorState.secondaryZoomCoordinates, secondaryImageRef]);

  // Helper function to get real-time markup URL (same as debug panel)
  const getRealTimeMarkupUrl = useCallback((markupUrl: string, isPrimary: boolean = true) => {
    if (!markupUrl) return null;
    
    const isCloudinary = markupUrl.includes('cloudinary.com') || markupUrl.includes('res.cloudinary.com');
    if (!isCloudinary) return markupUrl; // Return Supabase URLs unchanged
    
    const coordinates = isPrimary ? editorState.zoomCoordinates : editorState.secondaryZoomCoordinates;
    const imageElement = isPrimary ? imageRef.current : secondaryImageRef.current;
    
    return getViewportCropUrl(markupUrl, coordinates, undefined, imageElement || undefined);
  }, [editorState.zoomCoordinates, editorState.secondaryZoomCoordinates, imageRef, secondaryImageRef]);
  const handleSave = useCallback(async () => {
    try {
      editorState.setIsUploading(true);
      
      // Generate URLs using same logic as debug panel
      const primaryCroppedUrl = getPrimaryCropUrl();
      if (!primaryCroppedUrl) {
        toast({
          title: "Error",
          description: "No primary image available to save",
          variant: "destructive",
        });
        return;
      }
      
      // Handle markup URLs based on current mode and reset requests
      let primaryMarkupUrl = null;
      let secondaryMarkupUrl = null;
      
      if (editorState.primaryImageMode === 'crop' || editorState.primaryResetRequested?.current) {
        primaryMarkupUrl = null; // Clear markup in crop mode or when reset requested
      } else if (asset.primary_markup_url) {
        primaryMarkupUrl = getRealTimeMarkupUrl(asset.primary_markup_url, true);
      }
      
      // Handle annotations - overrides existing markup URLs
      if (annotationState.annotations.length > 0) {
        const originalImageUrl = getEffectivePrimaryOriginalUrl();
        primaryMarkupUrl = await generateAnnotatedImage(
          originalImageUrl, 
          annotationState.annotations, 
          editorState.zoomCoordinates, 
          editorState.selectedAspectRatio, 
          annotationState.annotationStyle,
          imageRef
        );
      }
      
      // Get versions
      const primaryVersion = editorState.primaryImageData?.version || localStorage.getItem(`asset_${asset.id}_primary_version`) || asset.version;
      
      // Check if dual view mode - if comparison mode is not 'single', save as diff type
      const isDualView = editorState.comparisonMode !== 'single';
      
      
      if (isDualView) {
        // Dual view save
        const secondaryCroppedUrl = getSecondaryCropUrl();
        const secondaryVersion = editorState.secondaryImageData?.version || localStorage.getItem(`asset_${asset.id}_secondary_version`) || null;
        
        if (editorState.secondaryImageMode === 'crop' || editorState.secondaryResetRequested?.current) {
          secondaryMarkupUrl = null;
        } else if (asset.secondary_markup_url) {
          secondaryMarkupUrl = getRealTimeMarkupUrl(asset.secondary_markup_url, false);
        }
        
        // Handle secondary annotations
        if (annotationState.annotations.length > 0 && secondaryCroppedUrl) {
          const secondaryOriginalUrl = getEffectiveSecondaryOriginalUrl();
          secondaryMarkupUrl = await generateAnnotatedImage(
            secondaryOriginalUrl, 
            annotationState.annotations, 
            editorState.secondaryZoomCoordinates, 
            editorState.selectedAspectRatio, 
            annotationState.annotationStyle,
            secondaryImageRef
          );
        }
        
        const updateData = {
          type: 'diff' as const,
          comparison_mode: mapComparisonModeToGraphQL(editorState.comparisonMode) as 'side_by_side' | 'stacked',
          version: primaryVersion,
          secondary_version: secondaryVersion,
          primary_cropped_url: primaryCroppedUrl,
          primary_markup_url: primaryMarkupUrl,
          secondary_original_url: getEffectiveSecondaryOriginalUrl(),
          secondary_cropped_url: secondaryCroppedUrl || undefined,
          secondary_markup_url: secondaryMarkupUrl,
          crop_aspect_ratio: editorState.selectedAspectRatio,
          
          // New explicit coordinate fields (backend will ignore if not supported)
          primary_crop_coordinates: editorState.primaryCropCoordinates,
          primary_markup_coordinates: editorState.primaryMarkupCoordinates,
          secondary_crop_coordinates: editorState.secondaryCropModeCoordinates,
          secondary_markup_coordinates: editorState.secondaryMarkupCoordinates,
          
          // LEGACY: Keep for backward compatibility (will be removed later)
          crop_coordinates: JSON.stringify({
            primary: {
              crop: editorState.primaryCropCoordinates,
              markup: editorState.primaryMarkupCoordinates,
              currentMode: editorState.primaryImageMode
            },
            secondary: {
              crop: editorState.secondaryCropModeCoordinates,
              markup: editorState.secondaryMarkupCoordinates,
              currentMode: editorState.secondaryImageMode
            },
            annotations: annotationState.annotations
          }),
          
          header: editorState.header,
          subheader: editorState.subheader
        };
        
        await updateAsset(asset.id, updateData);
        
        toast({
          title: "Success",
          description: `Dual view images saved successfully${annotationState.annotations.length > 0 ? ` with ${annotationState.annotations.length} annotation(s)` : ''}`,
        });
      } else {
        // Single view save
        const updateData = {
          type: 'simple' as const,
          comparison_mode: mapComparisonModeToGraphQL('single') as 'single',
          version: primaryVersion,
          primary_cropped_url: primaryCroppedUrl,
          primary_markup_url: primaryMarkupUrl,
          crop_aspect_ratio: editorState.selectedAspectRatio,
          
          // New explicit coordinate fields (backend will ignore if not supported)
          primary_crop_coordinates: editorState.primaryCropCoordinates,
          primary_markup_coordinates: editorState.primaryMarkupCoordinates,
          
          // LEGACY: Keep for backward compatibility (will be removed later)
          crop_coordinates: JSON.stringify({
            primary: {
              crop: editorState.primaryCropCoordinates,
              markup: editorState.primaryMarkupCoordinates,
              currentMode: editorState.primaryImageMode
            },
            ...editorState.zoomCoordinates,
            annotations: annotationState.annotations
          }),
          
          header: editorState.header,
          subheader: editorState.subheader
        };
        
        await updateAsset(asset.id, updateData);
        
        toast({
          title: "Success",
          description: annotationState.annotations.length > 0 
            ? `Image cropped and saved with ${annotationState.annotations.length} annotation(s)` 
            : "Image cropped and saved successfully",
        });
      }
      
      // Clear reset flags
      if (editorState.primaryResetRequested?.current) {
        editorState.primaryResetRequested.current = false;
      }
      if (editorState.secondaryResetRequested?.current) {
        editorState.secondaryResetRequested.current = false;
      }
      
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to crop and save image",
        variant: "destructive",
      });
    } finally {
      editorState.setIsUploading(false);
    }
  }, [
    editorState,
    annotationState,
    asset,
    getPrimaryCropUrl,
    getSecondaryCropUrl,
    getEffectivePrimaryOriginalUrl,
    getEffectiveSecondaryOriginalUrl,
    getRealTimeMarkupUrl,
    toast,
    onOpenChange,
    onSuccess,
    imageRef,
    secondaryImageRef
  ]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return {
    handleSave,
    handleCancel
  };
};